/**
 * セマンティックバージョン文字列の比較ユーティリティ。
 * 文字列の辞書順比較では "1.10" < "1.9" になるため、数値セグメントで比較する。
 */

/**
 * バージョン文字列を `.` で分割して数値配列に変換する。
 * 不正な入力（空文字・数値以外を含むセグメント）の場合は null を返す。
 */
const parseVersion = (version: string): number[] | null => {
  if (!version || typeof version !== "string") return null;
  const parts = version.split(".");
  const nums: number[] = [];
  for (const part of parts) {
    const n = Number(part);
    if (!Number.isInteger(n) || n < 0 || part.trim() === "") return null;
    nums.push(n);
  }
  return nums.length > 0 ? nums : null;
};

/**
 * 2つのバージョン文字列を比較する。
 *
 * @returns 負数: a < b / 0: a == b / 正数: a > b
 *          どちらかが不正なバージョン文字列の場合も 0 を返す（安全側フォールバック）。
 */
export const compareVersion = (a: string, b: string): number => {
  const aParts = parseVersion(a);
  const bParts = parseVersion(b);
  if (!aParts || !bParts) return 0;

  const maxLen = Math.max(aParts.length, bParts.length);
  for (let i = 0; i < maxLen; i++) {
    const aNum = aParts[i] ?? 0;
    const bNum = bParts[i] ?? 0;
    if (aNum !== bNum) return aNum - bNum;
  }
  return 0;
};

/**
 * version が threshold より低いかどうかを返す。
 * 不正なバージョン文字列の場合は false を返す（ブロックしない方向に倒す）。
 *
 * @example isVersionBelow("1.0", "1.1") // true
 * @example isVersionBelow("1.1", "1.1") // false
 * @example isVersionBelow("1.1.0", "1.1") // false
 */
export const isVersionBelow = (version: string, threshold: string): boolean => {
  return compareVersion(version, threshold) < 0;
};
